import { InstanceAPIType, AuthUserType } from "../../../Actions/Types"
import { Reducer } from "redux"
import { AuthAction } from "../../Auth"
import {
    APIGroundTruthModel,
    getGroundTruthID,
    APIGroundTruthDetailedModel
} from "../../../Models/API/APIGroundTruthModel"
import { RouterAction, LOCATION_CHANGE } from "connected-react-router"
import { matchPath } from "react-router"
import { abstractRoutePaths } from "../../../Routes"
import {
    getInitialData,
    setPage,
    RemoteData,
    markAllPagesOfAllEntriesAsStale
} from "../../../Models/Client/RemoteData"
import { Result } from "../../../Models/Utility/Result"
import { FilterAndSortExpression } from "../../../Models/Client/FilterAndSortExpression"
import { updateAt } from "../../../Helpers/Functions/reducer"
import { FetchState, hasValue } from "../../../Models/Client/FetchState"

export interface AppInstanceBaselineDataState {
    table: {
        [appID: string]:
            | RemoteData<
                  APIGroundTruthModel,
                  FilterAndSortExpression<APIGroundTruthModel>,
                  string
              >
            | undefined
    }
    totalCounts: {
        [appID: string]: FetchState<{ value: number; isStale: boolean }>
    }
    detailedData: {
        [baselineID: string]: FetchState<APIGroundTruthDetailedModel>
    }
    isShowingJobDetailPage: boolean
}

interface BaselineDataSetList {
    type: InstanceAPIType.baselineData_setList
    meta: {
        total: number
        params: FilterAndSortExpression<APIGroundTruthModel>
        page: number
    }
    payload: {
        appID: string
        baselineData: APIGroundTruthModel[]
    }
}

interface BaselineDataSetOne {
    type: InstanceAPIType.baselineData_setOne
    payload: {
        instanceID: string
        baselineID: string
        baselineData: APIGroundTruthDetailedModel
    }
}

interface BaselineDataSetError {
    type: InstanceAPIType.baselineData_setError
    meta: {
        appID: string
        groundTruthID: string
    }
    payload: string // message
}

interface BaselineDataSetTotalCount {
    type: InstanceAPIType.baselineData_setTotalCount
    meta: {
        appID: string
    }
    payload: number
}

export type AppInstanceBaselineDataAction =
    | BaselineDataSetList
    | BaselineDataSetOne
    | BaselineDataSetError
    | BaselineDataSetTotalCount

const initialState: AppInstanceBaselineDataState = {
    table: {},
    totalCounts: {},
    isShowingJobDetailPage: false,
    detailedData: {}
}

export const AppInstanceBaselineDataReducer: Reducer<
    AppInstanceBaselineDataState,
    AppInstanceBaselineDataAction | AuthAction | RouterAction
> = (state = initialState, action): AppInstanceBaselineDataState => {
    switch (action.type) {
        case LOCATION_CHANGE:
            const isEnteringDetailPage =
                matchPath(action.payload.location.pathname, {
                    path: abstractRoutePaths.appCustomizeBaselineDataDetail,
                    exact: true
                }) !== null
            // we don't want to trigger refreshing groundtruth data list when entering/leaving
            // detail page, as it is a modal screen with groundtruth data list behind it.
            const shouldMarkListAsStale =
                !isEnteringDetailPage && !state.isShowingJobDetailPage

            return {
                ...state,
                table: shouldMarkListAsStale
                    ? markAllPagesOfAllEntriesAsStale(state.table)
                    : state.table,
                totalCounts: Object.entries(state.totalCounts).reduce(
                    (passed, [appID, d]) => ({
                        ...passed,
                        [appID]: hasValue(d)
                            ? Result.ok({
                                  ...d.value,
                                  isStale: d.isStale || shouldMarkListAsStale
                              })
                            : d
                    }),
                    {}
                ),
                isShowingJobDetailPage: isEnteringDetailPage
            }
        case InstanceAPIType.baselineData_setList: {
            return {
                ...state,
                table: updateAt(state.table, action.payload.appID, table =>
                    setPage(
                        table ?? getInitialData(),
                        action.meta.params,
                        action.payload.baselineData,
                        getGroundTruthID,
                        action.meta.page,
                        action.meta.total
                    )
                )
            }
        }
        case InstanceAPIType.baselineData_setOne: {
            const data =
                state.table[action.payload.instanceID] || getInitialData()

            return {
                ...state,
                table: {
                    ...state.table,
                    [action.payload.instanceID]: {
                        ...data,
                        records: data.records.set(
                            String(action.payload.baselineID),
                            Result.ok(action.payload.baselineData)
                        )
                    }
                },
                detailedData: {
                    ...state.detailedData,
                    [action.payload.baselineID]: Result.ok(
                        action.payload.baselineData
                    )
                }
            }
        }
        case InstanceAPIType.baselineData_setError: {
            const data = state.table[action.meta.appID] || getInitialData()

            return {
                ...state,
                table: {
                    ...state.table,
                    [action.meta.appID]: {
                        ...data,
                        records: data.records.set(
                            action.meta.groundTruthID,
                            Result.error(action.payload)
                        )
                    }
                }
            }
        }
        case InstanceAPIType.baselineData_setTotalCount: {
            return {
                ...state,
                totalCounts: updateAt(
                    state.totalCounts,
                    action.meta.appID,
                    () => Result.ok({ value: action.payload, isStale: false })
                )
            }
        }
        case AuthUserType.signedOut:
            return initialState
        default:
            return state
    }
}
